import { makeAutoObservable } from 'mobx';
import React from 'react';
import { CURRENT_VERSION, migrateToTheLatest } from 'shared/migration/user';
import { Plan } from 'shared/services/license';
import { Account, LoginData } from 'shared/types/authorization';
import { AccountType } from 'shared/types/flowstep';
import { CancelToken } from 'shared/utils/cancelToken';
import {
  emitDebug,
  getRedirectionUrl,
  getSlackChannels,
  getUser,
  patchUserSettings,
  refreshToken,
  slackLogout
} from 'src/api';
import { ws } from 'src/api/ws';
import { Type } from 'src/components/base/MessagePane';
import { BrowserStorage } from 'src/services/browserStorage';
import { getSubscribeLink, License } from 'src/services/license';
import office from 'src/services/office';
import { openPopupWindow } from 'src/services/tools';
import RequestStore from 'src/stores/requestStore';
import { request } from "@v2/api/utils";

const STORAGE_PREFIX = '__excel365_';

const Storage = {
  User: `${STORAGE_PREFIX}user`,
  Accounts: `${STORAGE_PREFIX}accounts`,
};

const setUserVersion = (user?: Account) => ({
  ...user,
  version: CURRENT_VERSION,
});

export default class UserStore {
  license: License = new License();
  slackChannels: any[] = [];
  loggingInUser: LoginData | null = null;
  //for migration rollback
  logginInUserOldData: any = null;
  loggedInUser: LoginData | null = null;
  lastLoggedOutUser: LoginData | null = null;
  accounts: Account[] = [];
  requestStore!: RequestStore;

  // workaround for SignIn form
  showPageFooter = true;

  private browserStorage: BrowserStorage = new BrowserStorage();

  constructor() {
    makeAutoObservable(this);
  }

  linkStores(stores: any) {
    Object.assign(this, stores);
  }

  get token() {
    return this.user?.loginData.token;
  }

  get isAuthorized() {
    return !!this.user && !!this.user.loginData.token;
  }

  get settings() {
    return this.user?.settings;
  }

  get userName() {
    return this.user?.username;
  }

  get version() {
    return this.user?.sfData?.sf_api;
  }

  get brand() {
    return this.user?.license?.brand;
  }

  get organizationType() {
    return this.user?.sfData?.organizationType;
  }

  get isSlackAuthorised() {
    return !!this.user?.slackData;
  }

  get sfMode() {
    return this.user?.loginData.sfMode;
  }

  get accountType() {
    return this.user?.loginData.accountType;
  }

  get user() {
    return this.loggedInUser ? this.getAccount(this.loggedInUser) : null;
  }

  get userId() {
    return `${this.userName} ${this.sfMode}`;
  }

  get getAccountIndex() {
    return (data: LoginData) => {
      return this.accounts.findIndex(
        (account) =>
          account.username === data.username &&
          account.loginData.accountType === data.accountType &&
          account.loginData.sfMode === data.sfMode,
      );
    };
  }

  get getAccount() {
    return (data: LoginData) => {
      const index = this.getAccountIndex(data);
      return index === -1 ? null : this.accounts[index];
    };
  }

  setShowPageFooter(value: boolean) {
    this.showPageFooter = value;
  }

  initialize() {
    let user = this.fetchUserFromLS();

    const accounts = this.fetchAccountsFromLS();
    if (user) {
      const { loginData: { accountType: provider } = {}, sfData: { mode } = {} } = user;

      if (provider === AccountType.SF && !mode) {
        user = null;
      }
    }

    if (!accounts.length && user) {
      accounts.push(user);
    }

    this.setAccounts(accounts);
    this.loggingInUser = user ? user.loginData : null;
    this.logginInUserOldData = user ? user.old_data : null;
  }

  async signInOrUp(data: LoginData, useBrowser: boolean, cancelToken: CancelToken) {
    const user = this.getAccount(data);
    this.loggingInUser = user?.loginData || null;
    this.logginInUserOldData = user ? user.old_data : null;

    if (!(await this.tryToSignInWithToken())) {
      await this.signInOrUpWithoutToken(data, useBrowser, cancelToken);
    }

    this.lastLoggedOutUser = null;
  }

  async updateCurrentUser() {
    this.loggingInUser = this.loggedInUser;
    if (this.loggingInUser) {
      const user = this.getAccount(this.loggingInUser);
      this.logginInUserOldData = user ? user.old_data : null;
    }

    await this.tryToSignInWithToken();
  }

  async tryToSignInWithToken() {
    const { token } = this.loggingInUser ?? {};
    if (token) {
      let user: Account | null = null;
      try {
        user = await getUser(token, {
          office: btoa(JSON.stringify(Office.context.diagnostics)),
        });
      } catch (e) {
        // token_expired
      }

      if (user) {
        user.loginData = { ...this.loggingInUser, ...user.loginData };
        user.old_data = this.logginInUserOldData;
        const loggingInUser = user.loginData;
        this.loggingInUser = loggingInUser;
        await this.addOrUpdateAccount(user);
        return true;
      }
    }
    return false;
  }

  async signInOrUpWithoutToken(
    data: LoginData,
    useBrowser: boolean,
    cancelToken: CancelToken,
  ) {
    const { username } = data;

    if (username) {
      const { url, key } = await ws.action('refresh-token', data, { cancelToken });
      if (url) {
        const { license, ...user } = await ws.waitFor(key, { cancelToken }, async () => {
          if (data.accountType === AccountType.SF) {
            await refreshToken(url);
          } else {
            await request('GET', url, {}, '', undefined);
          }
        });
        user.license = license;
        await this.addOrUpdateAccount(user);
        return;
      }
    }

    const action = data.accountType === AccountType.SF ? 'sf-authorise' : 'hubspot-authorise';
    const { url, key } = await ws.action(action, data, { cancelToken });
    await openPopupWindow(getRedirectionUrl({ url }), { useBrowser });

    const { license, ...user } = await ws.waitFor(key, { cancelToken });
    user.license = license;
    await this.addOrUpdateAccount(user);
  }

  async signOut(rememberLastLoggedOutUser = false) {
    if (!this.user) {
      return;
    }

    if (rememberLastLoggedOutUser) {
      this.lastLoggedOutUser = this.user.loginData;
    }

    await this.logOut(this.user.loginData);
  }

  async updateSettings(data: any): Promise<void> {
    if (this.token) {
      const user = await patchUserSettings(data, this.token);
      await this.addOrUpdateAccount(user);
    }
  }

  async debug(message: string): Promise<void> {
    if (this.token) {
      await emitDebug({ message }, this.token);
    }
  }

  async loginToSlack(cancelToken: CancelToken) {
    const { key, url } = await ws.action('sl-authorise', {}, { cancelToken });
    await openPopupWindow(url);
    const { channels } = await ws.waitFor(key, { cancelToken });
    this.slackChannels = channels;
    if (this.user) this.user.slackData = {};
  }

  async logoutFromSlack() {
    if (this.token) {
      await slackLogout(this.token);
      this.slackChannels = [];
      if (this.user) this.user.slackData = undefined;
    }
  }

  async fetchSlackChannels() {
    if (this.token) {
      this.slackChannels = await getSlackChannels({}, this.token);
    }
    return this.slackChannels;
  }

  async setCurrentUser(user: Account | null) {
    this.loggingInUser = null;
    this.logginInUserOldData = null;
    this.loggedInUser = user ? user.loginData : null;
    this.license.dispose();
    this.license = new License(user?.license);

    if (!office.isPlatformiOS) {
      try {
        if (user?.loginData.token) {
          await OfficeRuntime.storage.setItem('token', user.loginData.token);
        } else {
          await OfficeRuntime.storage.removeItem('token');
        }
      } catch (e) {
        // Fails in Incognito mode in Chrome
      }
    }

    this.saveUserToLS(user);
  }

  async addOrUpdateAccount(user: Account) {
    await this.setCurrentUser(user);

    const index = this.getAccountIndex(user.loginData);
    if (index === -1) {
      this.setAccounts([...this.accounts, user]);
    } else {
      const updatedAccounts = [...this.accounts];
      updatedAccounts.splice(index, 1, user);
      this.setAccounts(updatedAccounts);
    }
  }

  async forgetAccount(data: LoginData) {
    if (
      data.username === this.userName &&
      data.accountType === this.accountType &&
      data.sfMode === this.sfMode
    ) {
      await this.setCurrentUser(null);
    }

    const index = this.getAccountIndex(data);
    if (index !== -1) {
      const updatedAccounts = [...this.accounts];
      updatedAccounts.splice(index, 1);
      this.setAccounts(updatedAccounts);
    }
  }

  async logOut(data: LoginData) {
    if (
      data.username === this.userName &&
      data.accountType === this.accountType &&
      data.sfMode === this.sfMode
    ) {
      await this.setCurrentUser(null);
    }
  }

  checkLicenseStatus() {
    if (this.license.plan === Plan.Trial) {
      this.checkTrialLicense();
    }
    if (this.license.plan === Plan.Free) {
      this.checkFreeLicense();
    }

  }

  checkTrialLicense() {
    const remainingDays = this.license.remainingDays;
    if (remainingDays > 5 || this.license.remainingDays === 0) {
      return;
    }

    const subscribeLink = getSubscribeLink(Plan.Admin, (this.license.rawLicense as any).activation_code);
    let message = <></>;
    let paneType = Type.Info;

    if (this.license.remainingDays <= 3) {
      paneType = Type.Warning;
      message = (
        <>
          Your trial ends in {remainingDays} days -{' '}
          <a href={subscribeLink} target="_blank" rel="noopener noreferrer">upgrade now</a>
          {' '}to keep uninterrupted access to all features
        </>
      );
      if (this.license.remainingDays === 1) {
        message = (
          <>
            Your trial ends tomorrow - {' '}
            <a href={subscribeLink} target="_blank" rel="noopener noreferrer">upgrade now</a>
            {' '}to keep uninterrupted access to all features
          </>
        );
      }
    }
    else {
      message = (
        <>
          Your trial ends in {remainingDays} days.{' '}
          <a href={subscribeLink} target="_blank" rel="noopener noreferrer">Upgrade now</a>
          {' '}to keep access to all premium features
        </>
      );
    }

    this.requestStore.showLicensePane({
      message,
      paneType,
      onUpgrade: () => {
        window.open(subscribeLink, '_blank');
      }
    });
  }

  checkFreeLicense() {
    this.requestStore.showLicenseOverlay({
      title: 'Your trial has expired',
      text: 'Upgrade now to keep uninterrupted access to premium features',
      button: {
        text: 'Upgrade now',
        onClick: () => {
          window.open(getSubscribeLink(Plan.Admin, (this.license.rawLicense as any).activation_code), '_blank');
        },
      }
    })
  }

  setAccounts(accounts: Account[]): void {
    this.accounts = accounts;
    this.saveAccountsToLS(accounts);
  }

  private fetchAccountsFromLS(): Account[] {
    const accounts = JSON.parse(this.browserStorage.getItem(Storage.Accounts) || '[]');
    return accounts ? migrateToTheLatest(accounts).objects : [];
  }

  private saveAccountsToLS(accounts: Account[]): void {
    this.browserStorage.setItem(Storage.Accounts, JSON.stringify(accounts.map(setUserVersion)));
  }

  private fetchUserFromLS(): Account | null {
    const account = JSON.parse(this.browserStorage.getItem(Storage.User) || 'null');
    return account ? migrateToTheLatest(account).objects[0] : null;
  }

  private saveUserToLS(user: Account | null): void {
    this.browserStorage.setItem(Storage.User, JSON.stringify(user ? setUserVersion(user) : null));
  }
}
